import i18n from 'i18next';
import debounce from 'lodash/debounce';
import merge from 'lodash/merge';
import { action, autorun, makeObservable, observable, reaction } from 'mobx';

import { DESKTOP_THRESHOLD, PRIMARY_HTML_ELEMENT_ID } from '../../config/constants';
import { getUserLanguage } from '../../i18n/helpers';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import soundEffects from '../../services/sounds/soundEffects';
import { BANNER_PAGES, type BannersData, takePrimedBanners, uiService } from '../../services/ui';
import { scrollTo } from '../../utils/scrollTo';
import { type RootStore } from '../RootStore';

import { LD_LIVE_BANNERS_FLAG_KEY, liveBannerLdValueToStoreData } from './liveBannerLaunchDarkly';
import {
  type LandingPos,
  type AnimationProps,
  type IOpenDialog,
  type FullPageDialogs,
  type Slide,
  type FullPageDialogsOptions,
  type EnumOpenDialog,
  type CoinsLandingPos,
  type ClaimRewardAnimationState,
  type LiveBannerData,
} from './types';

export class UiStore {
  private readonly rootStore: RootStore;
  private readonly startingHeight = window.innerHeight;
  private prevLang = getUserLanguage();
  private liveBannerExpiryTimeout: number | undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    this.bannerAnimationFlag = resolveFlag('enable_banner_rive_animation', true);

    ldClient?.on('ready', () => {
      if (!ldClient) return;
      const enableBannerRiveAnimation = !!ldClient.variation('enable_banner_rive_animation', true);
      this.updateBannerAnimationFlag(enableBannerRiveAnimation);

      const liveBannerVariation = ldClient.variation(LD_LIVE_BANNERS_FLAG_KEY, null);
      this.syncLiveBannerFromLaunchDarkly(liveBannerVariation);
    });

    ldClient?.on('change', (changes) => {
      if ('enable_banner_rive_animation' in changes) {
        this.updateBannerAnimationFlag(changes.enable_banner_rive_animation.current);
      }

      if (LD_LIVE_BANNERS_FLAG_KEY in changes && this.rootStore.authStore.authorized) {
        this.syncLiveBannerFromLaunchDarkly(changes[LD_LIVE_BANNERS_FLAG_KEY].current);
      }
    });

    void this.getBanners({ lang: getUserLanguage() });
    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang !== lang) {
        this.prevLang = lang;
        void this.getBanners({ lang });
      }
    });

    reaction(
      () => this.rootStore.authStore.authorized,
      () => {
        void this.getBanners({ lang: getUserLanguage() });
      },
    );

    autorun(() => {
      if (!this.rootStore.authStore.authorized) {
        this.setSideMenuOpen(false);
        this.resetLiveBannerData();
      } else {
        this.setCloseDialog();
        this.closeFullPageDialog();
        this.syncLiveBannerFromLaunchDarkly(resolveFlag(LD_LIVE_BANNERS_FLAG_KEY, null));
      }
    });

    autorun(() => {
      if (this.overflowBlockers.size > 0) {
        const scrollY = document.documentElement.scrollTop;
        document.body.style.top = `-${scrollY}px`;
        document.body.setAttribute('data-dialog', 'true');
      } else {
        const savedTop = document.body.style.top;
        const scrollY = savedTop ? Math.abs(parseFloat(savedTop)) : 0;
        document.body.setAttribute('data-dialog', 'false');
        document.body.style.top = '';
        document.documentElement.scrollTop = scrollY;
      }
    });

    window.addEventListener(
      'resize',
      debounce(() => {
        if (window.innerHeight === this.startingHeight) {
          this.setAddressBarVisible(true);
        } else {
          this.setAddressBarVisible(false);
        }
      }, 300),
    );
  }

  public landingPos: LandingPos | null = null;

  @observable
  public bannerAnimationFlag: boolean = false;

  @observable
  public CoinsAnimationVisible: boolean = false;

  @observable
  public isVisibleMiniStore: boolean = false;

  @observable
  public CoinsAnimationGCAmount: number | string = 0;

  @observable
  public CoinsAnimationSCAmount: number | string = 0;

  @observable
  public onGCCoinLandCallback: (() => void) | undefined = undefined;

  @observable
  public onSCCoinLandCallback: (() => void) | undefined = undefined;

  @observable
  public onEndCallback: (() => void) | undefined = undefined;

  @observable
  public animationProps: AnimationProps | undefined = undefined;

  @observable
  public addressBarVisible: boolean = true;

  @observable
  public sideMenuOpen: boolean = false;

  @observable
  public claimRewardAnimationState: ClaimRewardAnimationState = {
    enable: false,
    rewards: [],
  };

  private readonly blockedDialogs = new Set<EnumOpenDialog>([]);

  @observable
  public openDialog: IOpenDialog = {
    dialog: null,
  };

  @observable
  public openFullPageDialog: FullPageDialogs | null = null;
  @observable
  public openFullPageDialogOptions: FullPageDialogsOptions | null = null;

  @observable
  public prevSlide: Slide | null = null;

  @observable
  public requiredOpenNextDialog: IOpenDialog = {
    dialog: null,
  };

  @observable
  public overflowBlockers: Set<symbol> = new Set([]);

  @observable
  public disabledSounds: boolean = Boolean(localStorage.getItem('disable_sounds'));

  @observable
  public isChildDialogOpen: boolean = false;

  @observable
  public liveBannerData: LiveBannerData = {
    bannerKey: null,
    appearanceLimit: 0,
    showCloseButton: false,
    showLogo: false,
    bannerMessage: null,
    activatedAt: new Date(0),
    expiresAt: null,
  };

  @action
  public resetLiveBannerData() {
    if (this.liveBannerExpiryTimeout !== undefined) {
      clearTimeout(this.liveBannerExpiryTimeout);
      this.liveBannerExpiryTimeout = undefined;
    }
    this.liveBannerData = {
      bannerKey: null,
      appearanceLimit: 0,
      showCloseButton: false,
      showLogo: false,
      bannerMessage: null,
      activatedAt: new Date(0),
      expiresAt: null,
    };
  }

  /**
   * Reads evaluated `live-banners` JSON from the LaunchDarkly client (segment rules live in LD).
   * Called after auth and when that flag changes.
   */
  @action
  public syncLiveBannerFromLaunchDarkly(raw: unknown) {
    if (this.liveBannerExpiryTimeout !== undefined) {
      clearTimeout(this.liveBannerExpiryTimeout);
      this.liveBannerExpiryTimeout = undefined;
    }
    if (!this.rootStore.authStore.authorized) {
      this.resetLiveBannerData();
      return;
    }

    const data = liveBannerLdValueToStoreData(raw);
    if (data == null) {
      this.resetLiveBannerData();
      return;
    }
    this.liveBannerData = data;
    if (data.expiresAt) {
      const delayMs = data.expiresAt.getTime() - Date.now();
      this.liveBannerExpiryTimeout = window.setTimeout(() => {
        this.liveBannerExpiryTimeout = undefined;
        this.resetLiveBannerData();
      }, delayMs);
    }
  }

  public get isDialogOpen() {
    return !!this.openDialog.dialog || !!this.openFullPageDialog;
  }

  @action
  public setIsVisibleMiniStore(shouldOpen: boolean) {
    if (shouldOpen) {
      this.isVisibleMiniStore = true;
    } else {
      this.isVisibleMiniStore = false;
    }
  }

  @action
  public setIsChildDialogOpen(open: boolean) {
    this.isChildDialogOpen = open;
  }

  @action
  public setClaimRewardAnimationState(state: Partial<ClaimRewardAnimationState>) {
    this.claimRewardAnimationState = { ...this.claimRewardAnimationState, ...state };
  }

  @action
  public startClaimRewardAnimation(args: Partial<Omit<ClaimRewardAnimationState, 'enable'>>) {
    if (this.claimRewardAnimationState.enable) {
      return;
    }

    this.setClaimRewardAnimationState({
      enable: true,
      onEnd: args.onEnd,
      rewards: args.rewards,
      refetchData: args.refetchData,
      enableSounds: args.enableSounds,
      startMiniGames: args.startMiniGames,
      hideOverlay: args.hideOverlay,
    });
  }

  @action
  public startCoinsAnimation(args: {
    gcAmount: number | string;
    scAmount: number | string;
    onGcCoin?: () => void;
    onScCoin?: () => void;
    onEnd?: () => void | Promise<void>;
    animationProps?: AnimationProps;
  }) {
    this.CoinsAnimationVisible = true;
    this.CoinsAnimationGCAmount = args.gcAmount;
    this.CoinsAnimationSCAmount = args.scAmount;
    this.onGCCoinLandCallback = args?.onGcCoin;
    this.onSCCoinLandCallback = args?.onScCoin;
    this.onEndCallback = args?.onEnd;
    this.animationProps = args?.animationProps;
  }

  getElementFixedXY(id: string) {
    const element = document.getElementById(id);
    if (!element) {
      return {
        x: 0,
        y: 0,
      };
    }
    return element.getBoundingClientRect();
  }

  public setCustomLandingCoinAnimation(landingPos: LandingPos) {
    this.landingPos = landingPos;
  }

  public get coinsLandingPos(): CoinsLandingPos {
    if (this.landingPos) {
      return {
        gcLandingPoint: this.landingPos.gcLandingPoint,
        scLandingPoint: this.landingPos.scLandingPoint,
      };
    }

    const isGC = this.rootStore.coinTypeStore.coinType === 'GC';
    const landingPos = isGC ? ['balance', 'switcher'] : ['switcher', 'balance'];

    const gcLandingPoint = this.getElementFixedXY(landingPos[0]);
    const scLandingPoint = this.getElementFixedXY(landingPos[1]);

    return {
      gcLandingPoint: {
        x: gcLandingPoint.x ?? 0,
        y: gcLandingPoint.y ?? 0,
      },
      scLandingPoint: {
        x: scLandingPoint.x ?? 0,
        y: scLandingPoint.y ?? 0,
      },
    };
  }

  @action
  public stopCoinsAnimation() {
    this.CoinsAnimationVisible = false;
    this.CoinsAnimationGCAmount = 0;
    this.CoinsAnimationSCAmount = 0;
    this.onGCCoinLandCallback = undefined;
    this.onSCCoinLandCallback = undefined;
    this.onEndCallback = undefined;
    this.animationProps = undefined;
  }

  @action
  public setDisabledSounds(disable: boolean) {
    if (disable) {
      localStorage.setItem('disable_sounds', '1');
    } else {
      localStorage.removeItem('disable_sounds');
    }
    this.disabledSounds = disable;
  }

  @action
  private setAddressBarVisible(visible: boolean) {
    this.addressBarVisible = visible;
  }

  @action
  public setSideMenuOpen(open: boolean) {
    this.sideMenuOpen = open;
    soundEffects.playSound(open ? 'popup-open' : 'popup-close');
  }

  @action
  public blockDialog(dialog: EnumOpenDialog) {
    this.blockedDialogs.add(dialog);

    if (this.openDialog.dialog === dialog) {
      this.openDialog = { dialog: null };
    }
  }

  public unblockDialog(dialog: EnumOpenDialog) {
    this.blockedDialogs.delete(dialog);
  }

  @action
  public setOpenDialog(data: IOpenDialog) {
    if (data.dialog != null && this.blockedDialogs.has(data.dialog)) {
      return;
    }

    this.openDialog = data;
    soundEffects.playSound('popup-open');
  }

  @action
  public setCloseDialog(silent: boolean = false) {
    this.openDialog = { dialog: null };
    if (!silent) {
      soundEffects.playSound('popup-close');
    }
  }

  @action
  public setOpenFullPageDialog(dialog: FullPageDialogs | null, options?: FullPageDialogsOptions) {
    if (this.openFullPageDialog !== dialog) {
      this.prevSlide = this.openFullPageDialog || 'home';
    }

    // If clicked on "Home" while already on "Home", scroll to top
    if (dialog === null && this.openFullPageDialog === null) {
      scrollTo(PRIMARY_HTML_ELEMENT_ID, 0);
    }

    this.openFullPageDialog = dialog;
    this.openFullPageDialogOptions = options || null;
    soundEffects.playSound('popup-open');

    if (dialog === 'new_tier_congrats') {
      soundEffects.playSound('new-tier-congrats', true);
    }
  }

  @action
  public closeFullPageDialog(silent: boolean = false) {
    this.prevSlide = this.openFullPageDialog;
    this.openFullPageDialog = null;
    if (!silent) {
      soundEffects.playSound('popup-close');
    }
  }

  @action
  public flushPrevSlide() {
    this.prevSlide = null;
  }

  @action
  public setRequiredNextDialog(data: IOpenDialog) {
    this.requiredOpenNextDialog = data;
  }

  public setOpenRequiredNextDialog() {
    this.setOpenDialog(this.requiredOpenNextDialog);
    this.setRequiredNextDialog({ dialog: null });
  }

  @action
  public setOverflowBlocker(key: symbol, value: boolean) {
    if (value) {
      this.overflowBlockers.add(key);
    } else {
      this.overflowBlockers.delete(key);
    }
  }

  @observable
  public bannerByPage: BannersData = {};
  @observable
  public bannersLoading: boolean = false;

  @action
  private setBanners(bannerByPage: BannersData, replace?: boolean) {
    if (replace) {
      this.bannerByPage = bannerByPage;
    } else {
      merge(this.bannerByPage, bannerByPage);
    }
  }

  @action
  private setBannersLoading(loading: boolean) {
    this.bannersLoading = loading;
  }

  @action
  updateBannerAnimationFlag(value: boolean) {
    this.bannerAnimationFlag = value;
  }

  private preloadBanners(banners: BannersData) {
    if (!this.bannerAnimationFlag) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const isMobile = window.matchMedia(`(max-width: ${DESKTOP_THRESHOLD}px)`).matches;
    BANNER_PAGES.forEach((type) => {
      const banner = banners[type];
      if (!banner) return;

      const { desktopBanner, mobileBanner } = banner;
      const animationUrl = isMobile
        ? mobileBanner?.animationUrl || desktopBanner.animationUrl
        : desktopBanner.animationUrl;

      if (animationUrl) {
        this.rootStore.riveFileStore.addFileToPreload(animationUrl);
      }
    });
  }

  public async getBanners(options?: { lang?: string; theme?: string }) {
    this.setBannersLoading(true);

    try {
      // Adopt the request render.tsx already issued, when there is one for these exact options —
      // it was started before LaunchDarkly init and the first render, so the banner URL (and the
      // LCP image behind it) is usually resolved by the time this store is constructed.
      const response = await (takePrimedBanners(options) ?? uiService.getBanners(options));

      const { banners } = response.data;
      this.preloadBanners(banners);
      this.setBanners(banners);
    } catch {
    } finally {
      this.setBannersLoading(false);
    }
  }
}
